import { useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { BellRing, CheckCircle2 } from "lucide-react";

import { attendanceService, type AttendanceAlert, type AttendanceAnalytics } from "../../services/attendanceService";
import { ReasonPanel, errorLine } from "../inventory/InventoryParts";
import { ClientRegister, StatusDot, type RegisterColumn } from "../register/ClientRegister";
import { DetailsModal } from "../register/DetailsModal";
import { Notice, type NoticeState } from "../register/RecordParts";
import { ALERT_TYPES, PRIORITIES, alertConfig, alertStatus, classRateConfig, day, dayRateConfig, daysWithRegisters, labelFrom, lowLearnerConfig, rateMeaning, rateStatus, registerLabel, statusDot, type ClassRate, type DayRate, type LowLearner } from "./attendanceLogic";

/** One learner's attendance over the last 90 days, month by month, with their latest marks. */
function LearnerHistory({ studentId }: { studentId: number }) {
  const history = useQuery({ queryKey: ["attendance", "learner", studentId], queryFn: () => attendanceService.getStudentAttendanceAnalytics(studentId, 90) });
  if (history.isLoading) return <p className="text-sm text-gray-500">Loading their attendance…</p>;
  if (history.isError || !history.data) return errorLine(history.error, "Their attendance could not be loaded.");
  const { statistics, monthly_breakdown, recent_attendance } = history.data;
  return (
    <>
      <section className="space-y-2">
        <h4 className="text-xs font-semibold uppercase tracking-wide text-gray-500">Last 90 days</h4>
        <p className="text-sm text-gray-700">
          {statistics.present_days} present of {statistics.total_days} registers · {statistics.absent_days} away · {statistics.late_days} late · {statistics.excused_days} excused.
          <span className="text-gray-500"> Their own rate counts late as 0.8 of a day and leaves excused days out, so it can differ from the list.</span>
        </p>
        <div className="sheet-wrap">
          <table className="sheet">
            <thead>
              <tr>
                <th>Month</th>
                <th className="text-right">Present</th>
                <th className="text-right">Registers</th>
                <th>Rate</th>
              </tr>
            </thead>
            <tbody>
              {monthly_breakdown.map((row) => (
                <tr key={row.month}>
                  <td className="text-gray-900">{row.month}</td>
                  <td className="text-right tabular-nums">{row.present_days}</td>
                  <td className="text-right tabular-nums">{row.total_days}</td>
                  <td>{row.total_days ? <StatusDot {...rateStatus(row.percentage)} /> : <span className="text-gray-400">No registers</span>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
      {recent_attendance.length > 0 && (
        <section className="space-y-2">
          <h4 className="text-xs font-semibold uppercase tracking-wide text-gray-500">Latest marks</h4>
          <ul className="divide-y divide-gray-100 rounded-lg border border-gray-200">
            {recent_attendance.map((row) => (
              <li key={row.id} className="flex flex-wrap items-center justify-between gap-2 px-3 py-2 text-sm">
                <span>
                  <span className="tabular-nums text-gray-500">{day(row.date)}</span>
                  <span className="ml-2 text-gray-700">{registerLabel(row)}</span>
                  {row.remarks && <span className="ml-2 text-gray-500">· {row.remarks}</span>}
                </span>
                <StatusDot {...statusDot(row.status)} />
              </li>
            ))}
          </ul>
        </section>
      )}
    </>
  );
}

/** Learners whose rate over the period is under 80%: a prompt to look, not a conclusion. */
export function LearnersPanel({ learners, days, isLoading, isError, onRetry }: { learners?: LowLearner[]; days: number; isLoading: boolean; isError: boolean; onRetry: () => void }) {
  const [notice, setNotice] = useState<NoticeState>(null);
  const [viewing, setViewing] = useState<LowLearner | null>(null);
  const notify = useMutation({
    mutationFn: (row: LowLearner) => attendanceService.notifyPoorAttendance(row.student_id),
    onSuccess: (_data, row) => setNotice({ tone: "success", text: `${row.student_name}'s parent has been sent their attendance for the last ${days} days.` }),
  });
  const columns: RegisterColumn<LowLearner>[] = [
    {
      header: "Learner",
      cell: (row) => (
        <span>
          <span className="font-medium text-gray-900">{row.student_name}</span>
          <span className="block text-xs text-gray-500">{row.student_id}</span>
        </span>
      ),
      exportValue: (row) => row.student_name,
      className: "min-w-[11rem]",
    },
    { header: "Learner number", cell: (row) => row.student_id, exportValue: (row) => row.student_id, exportOnly: true },
    { header: "Class", cell: (row) => row.class_name, exportValue: (row) => row.class_name, className: "text-gray-700" },
    { header: "Present", cell: (row) => `${row.present_days} of ${row.total_days}`, exportValue: (row) => row.present_days, align: "right", className: "tabular-nums text-gray-700" },
    { header: "Registers", cell: (row) => row.total_days, exportValue: (row) => row.total_days, exportOnly: true },
    { header: "Away", cell: (row) => row.total_days - row.present_days, exportValue: (row) => row.total_days - row.present_days, align: "right", className: "tabular-nums text-gray-700" },
    { header: "Rate", cell: (row) => <StatusDot {...rateStatus(row.percentage)} />, exportValue: (row) => row.percentage },
  ];
  return (
    <div className="space-y-4">
      <Notice notice={notice} onDismiss={() => setNotice(null)} />
      <ClientRegister
        title={`Learners below 80% (last ${days} days)`}
        description="A low rate has many causes: this is a prompt to look, not a conclusion. Open a learner for their months and latest marks."
        rows={learners}
        config={lowLearnerConfig}
        columns={columns}
        rowKey={(row) => row.id}
        onOpen={(row) => { notify.reset(); setViewing(row); }}
        noun={["learner", "learners"]}
        exportName={{ file: `attendance-below-80-${days}-days`, title: `Learners below 80% attendance, last ${days} days` }}
        isLoading={isLoading}
        isError={isError}
        onRetry={onRetry}
        emptyText="No learner with registers is below 80% in this period."
      />
      {viewing && (
        <DetailsModal
          size="3xl"
          title={viewing.student_name}
          headline={{ label: [viewing.class_name, viewing.student_id].filter(Boolean).join(" · "), value: `${viewing.percentage.toFixed(1)}%`, note: `Present ${viewing.present_days} of ${viewing.total_days} registers in the last ${days} days` }}
          status={rateMeaning(viewing.percentage)}
          facts={[["Away", String(viewing.total_days - viewing.present_days)], ["Registers", String(viewing.total_days)]]}
          onClose={() => setViewing(null)}
          footer={
            <>
              <button type="button" className="btn btn-outline" onClick={() => setViewing(null)}>Close</button>
              <button type="button" disabled={notify.isPending || notify.isSuccess} onClick={() => notify.mutate(viewing)} className="btn btn-primary gap-1.5" title="Sends the parent this rate for the period.">
                <BellRing className="h-4 w-4" /> {notify.isSuccess ? "Parent told" : "Tell the parent"}
              </button>
            </>
          }
        >
          {notify.isError && errorLine(notify.error, "The parent could not be told.")}
          <LearnerHistory studentId={viewing.id} />
        </DetailsModal>
      )}
    </div>
  );
}

/** Each class's rate over the period, and each day's across the school. */
export function ClassesPanel({ analytics, isLoading, isError, onRetry }: { analytics?: AttendanceAnalytics; isLoading: boolean; isError: boolean; onRetry: () => void }) {
  const classColumns: RegisterColumn<ClassRate>[] = [
    { header: "Class", cell: (row) => <span className="font-medium text-gray-900">{row.class_name}</span>, exportValue: (row) => row.class_name },
    { header: "Present", cell: (row) => row.present, exportValue: (row) => row.present, align: "right", className: "tabular-nums text-gray-700" },
    { header: "Marks", cell: (row) => row.total, exportValue: (row) => row.total, align: "right", className: "tabular-nums text-gray-700" },
    { header: "Rate", cell: (row) => (row.total ? <StatusDot {...rateStatus(row.percentage)} /> : <span className="text-gray-400">No registers</span>), exportValue: (row) => (row.total ? row.percentage : "") },
  ];
  const dayColumns: RegisterColumn<DayRate>[] = [
    { header: "Day", cell: (row) => new Date(`${row.date}T00:00:00`).toLocaleDateString(undefined, { weekday: "short", day: "numeric", month: "short", year: "numeric" }), exportValue: (row) => String(row.date), className: "whitespace-nowrap text-gray-900" },
    { header: "Present", cell: (row) => row.present, exportValue: (row) => row.present, align: "right", className: "tabular-nums text-gray-700" },
    { header: "Marks", cell: (row) => row.total, exportValue: (row) => row.total, align: "right", className: "tabular-nums text-gray-700" },
    { header: "Rate", cell: (row) => <StatusDot {...rateStatus(row.percentage)} />, exportValue: (row) => row.percentage },
  ];
  const days = analytics?.period.days ?? 30;
  return (
    <div className="space-y-6">
      <ClientRegister
        title={`By class (last ${days} days)`}
        description="Present, late and half day count as present. Classes without registers are listed last."
        rows={analytics?.class_statistics}
        config={classRateConfig}
        columns={classColumns}
        rowKey={(row) => row.class_name}
        noun={["class", "classes"]}
        exportName={{ file: `attendance-by-class-${days}-days`, title: `Attendance by class, last ${days} days` }}
        isLoading={isLoading}
        isError={isError}
        onRetry={onRetry}
        emptyText="No classes to show."
      />
      <ClientRegister
        title="By day"
        description="Every day that had registers, across the classes you can see."
        rows={analytics ? daysWithRegisters(analytics.daily_trends) : undefined}
        config={dayRateConfig}
        columns={dayColumns}
        rowKey={(row) => String(row.date)}
        noun={["day", "days"]}
        exportName={{ file: `attendance-by-day-${days}-days`, title: `Attendance by day, last ${days} days` }}
        isLoading={isLoading}
        isError={isError}
        onRetry={onRetry}
        emptyText="No registers were taken in this period."
      />
    </div>
  );
}

/** Alerts raised from the registers, and how each was followed up. */
export function AlertsPanel({ alerts, isLoading, isError, onRetry, onChanged }: { alerts?: AttendanceAlert[]; isLoading: boolean; isError: boolean; onRetry: () => void; onChanged: () => void }) {
  const [notice, setNotice] = useState<NoticeState>(null);
  const [viewing, setViewing] = useState<number | null>(null);
  const resolve = useMutation({
    mutationFn: ({ id, notes }: { id: number; notes: string }) => attendanceService.resolveAttendanceAlert(id, notes),
    onSuccess: () => { setNotice({ tone: "success", text: "The alert is resolved; your note stays with it." }); onChanged(); },
  });
  const columns: RegisterColumn<AttendanceAlert>[] = [
    { header: "Raised", cell: (row) => day(row.created_at), exportValue: (row) => day(row.created_at), className: "whitespace-nowrap tabular-nums text-gray-700" },
    {
      header: "Learner",
      cell: (row) => (
        <span>
          <span className="font-medium text-gray-900">{row.student_name}</span>
          <span className="block text-xs text-gray-500">{[row.class_name, row.student_id].filter(Boolean).join(" · ")}</span>
        </span>
      ),
      exportValue: (row) => row.student_name,
      className: "min-w-[11rem]",
    },
    { header: "Class", cell: (row) => row.class_name, exportValue: (row) => row.class_name, exportOnly: true },
    { header: "Kind", cell: (row) => labelFrom(ALERT_TYPES, row.alert_type), exportValue: (row) => labelFrom(ALERT_TYPES, row.alert_type), className: "text-gray-700" },
    { header: "Why", cell: (row) => row.message, exportValue: (row) => row.message, className: "max-w-[20rem] truncate text-gray-700" },
    { header: "Rate", cell: (row) => `${Number(row.attendance_percentage).toFixed(1)}%`, exportValue: (row) => Number(row.attendance_percentage), align: "right", className: "tabular-nums text-gray-700" },
    { header: "Status", cell: (row) => <StatusDot {...alertStatus(row)} />, exportValue: (row) => alertStatus(row).label },
    { header: "Follow-up", cell: (row) => row.resolution_notes ?? "", exportValue: (row) => row.resolution_notes ?? "", exportOnly: true },
  ];
  const alert = viewing !== null ? alerts?.find((row) => row.id === viewing) : undefined;
  return (
    <div className="space-y-4">
      <Notice notice={notice} onDismiss={() => setNotice(null)} />
      <ClientRegister
        title="Alerts"
        description="Raised when a register is saved: away three school days running, or under 80% over 30 days. Open one to follow it up."
        rows={alerts}
        config={alertConfig}
        columns={columns}
        rowKey={(row) => row.id}
        onOpen={(row) => { resolve.reset(); setViewing(row.id); }}
        openLabel={(row) => (row.is_resolved ? "Open" : "Follow up")}
        noun={["alert", "alerts"]}
        exportName={{ file: "attendance-alerts", title: "Attendance Alerts" }}
        isLoading={isLoading}
        isError={isError}
        onRetry={onRetry}
        emptyText="No alerts. They are raised as registers are saved."
      />
      {alert && (
        <DetailsModal
          size="3xl"
          title={alert.student_name}
          headline={{ label: [alert.class_name, alert.student_id].filter(Boolean).join(" · "), value: labelFrom(ALERT_TYPES, alert.alert_type), note: `${labelFrom(PRIORITIES, alert.priority)} priority · raised ${day(alert.created_at)}` }}
          status={alertStatus(alert)}
          facts={[
            ["Rate", `${Number(alert.attendance_percentage).toFixed(1)}%`],
            ["Period", `${day(alert.period_start)} to ${day(alert.period_end)}`],
            ["Parent told", alert.parent_notified ? "Yes" : "No"],
            ["Resolved", alert.is_resolved ? `${alert.resolved_by_name || "—"}, ${day(alert.resolved_at)}` : undefined],
          ]}
          sections={[{ title: "Why it was raised", body: alert.message }, { title: "How it was followed up", body: alert.resolution_notes }]}
          onClose={() => setViewing(null)}
        >
          {!alert.is_resolved && (
            <ReasonPanel
              title="Resolve the alert"
              description="Say what was done. The note stays on the record."
              label="What was done"
              placeholder="e.g. Phoned the mother: the learner had malaria and is back on Monday."
              context={{ key: "attendance.note", params: { kind: "alert", id: alert.id } }}
              button="Resolve"
              busy={resolve.isPending}
              error={resolve.error}
              errorText="The alert could not be resolved."
              onSubmit={(notes) => resolve.mutate({ id: alert.id, notes })}
            />
          )}
          {alert.is_resolved && <p className="flex items-center gap-2 text-sm text-emerald-700"><CheckCircle2 className="h-4 w-4" /> Resolved.</p>}
          <LearnerHistory studentId={alert.student_pk} />
        </DetailsModal>
      )}
    </div>
  );
}
